import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

export interface PasswordValidation {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  isValid: boolean;
}

export const validatePassword = (password: string): PasswordValidation => {
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  return {
    hasMinLength,
    hasUppercase,
    hasNumber,
    isValid: hasMinLength && hasUppercase && hasNumber
  };
};

export const signUpWithEmail = async (email: string, password: string, username?: string, captchaToken?: string) => {
  if (!supabase) {
    throw new Error("Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
  }
  const validation = validatePassword(password);
  if (!validation.isValid) {
    throw new Error("Password must be at least 8 characters long, contain at least one uppercase letter, and at least one number.");
  }

  const options: { emailRedirectTo: string; captchaToken?: string; data?: any } = {
    emailRedirectTo: window.location.origin
  };
  if (captchaToken) {
    options.captchaToken = captchaToken;
  }
  if (username) {
    options.data = { 
      username,
      name: username,
      full_name: username,
      display_name: username
    };
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options
  });

  if (error) throw error;
  return data;
};

export const signInWithEmail = async (email: string, password: string, captchaToken?: string) => {
  if (!supabase) {
    throw new Error("Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
  }

  const options: { captchaToken?: string } = {};
  if (captchaToken) {
    options.captchaToken = captchaToken;
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
    options
  });

  if (error) throw error;
  return data;
};

export const resetPasswordForEmail = async (email: string, captchaToken?: string) => {
  if (!supabase) {
    throw new Error("Supabase is not configured.");
  }
  const options: { redirectTo: string; captchaToken?: string } = {
    redirectTo: `${window.location.origin}/reset-password`
  };
  if (captchaToken) {
    options.captchaToken = captchaToken;
  }
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, options);
  if (error) throw error;
  return data;
};

export const signInWithGoogle = async () => {
  if (!supabase) {
    throw new Error("Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables.");
  }
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin
    }
  });
  
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// Sync user stats to cloud and return merged/latest stats
export const computeActiveStreak = (wonDates: string[] = []) => {
  if (wonDates.length === 0) return 0;
  
  const sorted = [...wonDates].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  
  const lastWonDate = new Date(sorted[0]);
  lastWonDate.setUTCHours(0, 0, 0, 0);
  
  const diffTime = Math.abs(today.getTime() - lastWonDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays > 1) {
    return 0;
  }
  
  let currentStreak = 1;
  let currentDate = lastWonDate;
  
  for (let i = 1; i < sorted.length; i++) {
    const nextDate = new Date(sorted[i]);
    nextDate.setUTCHours(0, 0, 0, 0);
    
    const diff = Math.abs(currentDate.getTime() - nextDate.getTime());
    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    
    if (days === 1) {
      currentStreak++;
      currentDate = nextDate;
    } else if (days === 0) {
      // Skip duplicate days
    } else {
      break;
    }
  }
  
  return currentStreak;
};

export const syncCloudStats = async (user: any, localStats: any) => {
  if (!supabase || !user) return localStats;
  try {
    // 1. Check if user_stats table exists and has data
    const { data: cloudRow, error: fetchErr } = await supabase
      .from('user_stats')
      .select('stats, updated_at')
      .eq('user_id', user.id)
      .maybeSingle();

    let mergedStats = { ...localStats };

    if (!fetchErr && cloudRow?.stats) {
      const cloudStats = cloudRow.stats;
      // Merge unique wonDates & startedDates
      const allWonDates = Array.from(new Set([...(localStats.wonDates || []), ...(cloudStats.wonDates || [])]));
      const allStartedDates = Array.from(new Set([...(localStats.startedDates || []), ...(cloudStats.startedDates || [])]));
      
      // Combine distributions
      const combinedDistribution = [0, 0, 0, 0, 0, 0, 0, 0];
      for (let i = 0; i < 8; i++) {
        combinedDistribution[i] = Math.max(localStats.distribution?.[i] || 0, cloudStats.distribution?.[i] || 0);
      }
      
      const computedStreak = computeActiveStreak(allWonDates);

      mergedStats = {
        played: Math.max(localStats.played || 0, cloudStats.played || 0, allStartedDates.length),
        won: Math.max(localStats.won || 0, cloudStats.won || 0, allWonDates.length),
        streak: computedStreak,
        maxStreak: Math.max(localStats.maxStreak || 0, cloudStats.maxStreak || 0, computedStreak),
        distribution: combinedDistribution,
        wonDates: allWonDates,
        startedDates: allStartedDates
      };
    } else if (user.user_metadata?.stats) {
      // Fallback from metadata if table not setup yet
      const cloudStats = user.user_metadata.stats;
      const allWonDates = Array.from(new Set([...(localStats.wonDates || []), ...(cloudStats.wonDates || [])]));
      const allStartedDates = Array.from(new Set([...(localStats.startedDates || []), ...(cloudStats.startedDates || [])]));
      
      const computedStreak = computeActiveStreak(allWonDates);
      
      mergedStats = {
        played: Math.max(localStats.played || 0, cloudStats.played || 0, allStartedDates.length),
        won: Math.max(localStats.won || 0, cloudStats.won || 0, allWonDates.length),
        streak: computedStreak,
        maxStreak: Math.max(localStats.maxStreak || 0, cloudStats.maxStreak || 0, computedStreak),
        distribution: localStats.distribution || cloudStats.distribution || [0, 0, 0, 0, 0, 0, 0, 0],
        wonDates: allWonDates,
        startedDates: allStartedDates
      };
    }

    // Save merged stats to table if available
    try {
      await supabase.from('user_stats').upsert({
        user_id: user.id,
        stats: mergedStats,
        updated_at: new Date().toISOString()
      });
    } catch {
      // Ignore table error
    }

    // Also persist in user_metadata as fallback
    try {
      await supabase.auth.updateUser({
        data: { stats: mergedStats }
      });
    } catch {
      // Ignore metadata error
    }

    return mergedStats;
  } catch (err) {
    console.error('Error syncing cloud stats:', err);
    return localStats;
  }
};

